/**
 * layoutTransport — the server half of `layoutStore`.
 *
 * Layouts used to live only in `localStorage`, which meant an arrangement
 * someone built on their laptop did not exist on their desktop, and clearing
 * site data threw it away. The backend now keeps one JSON blob per
 * (user, scope):
 *
 *     GET    /user/ui-layouts/<kind>/<name>[/<entity_id>]
 *     PUT    /user/ui-layouts/<kind>/<name>[/<entity_id>]   { layout: {…} }
 *     DELETE /user/ui-layouts/<kind>/<name>[/<entity_id>]
 *
 * THE USER IS NOT IN THE PATH — the token is the user, and a layout endpoint
 * that took a user id would be an authorisation bug waiting to happen. That is
 * also why the remote scope is only `<kind>:<name>[:<entity_id>]` while the
 * local key still carries the user segment: `localStorage` is shared by
 * everyone who uses the browser, and the server's store is not.
 *
 * EVERY FUNCTION HERE IS BEST-EFFORT. A layout is a convenience; nothing about
 * the surface stops working when the network does. So the read reports "I do
 * not know" separately from "the server says nothing is stored" (those mean
 * opposite things when deciding whether to migrate a local layout up), and the
 * writes resolve `false` rather than rejecting.
 */

import apis from '../../utils/apis';
import logger from '../../utils/logger';

/** The backend's cap on one stored layout. Bigger than any real arrangement. */
export const MAX_LAYOUT_BYTES = 8192;

/** What identifies a layout on the server. No user: the token is the user. */
export interface RemoteLayoutScope {
  kind: string;
  name: string;
  entityId?: string | null;
}

/** The backend's own rule: lowercase slug, ≤64 chars. */
const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const MAX_ENTITY_ID = 100;

/**
 * Whether this scope is one the server will accept.
 *
 * Checked HERE rather than discovered as a 400: a scope that cannot be stored
 * is a programming error in a surface definition, and the useful behaviour is
 * to keep working locally and say so once in the log — not to fire a doomed
 * request on every layout change.
 */
export function isStorableScope(scope: RemoteLayoutScope): boolean {
  if (!SLUG.test(scope.kind) || !SLUG.test(scope.name)) return false;
  if (scope.entityId != null && String(scope.entityId).length > MAX_ENTITY_ID) return false;
  return true;
}

export function remoteLayoutPath(scope: RemoteLayoutScope): string {
  const parts = ['/user/ui-layouts', scope.kind, scope.name];
  if (scope.entityId) parts.push(encodeURIComponent(String(scope.entityId)));
  return parts.join('/');
}

/**
 * Bytes a layout would occupy, measured the way the backend measures it.
 *
 * `String.length` counts UTF-16 code units, which undercounts anything
 * non-ASCII — a layout is machine-written so it is all ASCII in practice, but
 * a widget id or a pane label could carry anything, and silently sending 9KB
 * to an 8KB endpoint is a 413 nobody would understand.
 */
export function layoutByteLength(layout: unknown): number {
  let json: string;
  try {
    json = JSON.stringify({ layout });
  } catch {
    return Number.POSITIVE_INFINITY;
  }
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  // eslint-disable-next-line no-undef
  return unescape(encodeURIComponent(json)).length;
}

/**
 * The result of a read.
 *
 * `ok: false` is "we could not ask" — offline, 500, timeout. `ok: true` with
 * `layout: null` is the server's own answer: this user has never saved this
 * scope. The reconciliation in `layoutStore` treats them completely
 * differently, so collapsing them into `null` would either lose a local
 * arrangement or upload one over a newer server copy.
 */
export type RemoteReadResult =
  | { ok: true; layout: unknown | null }
  | { ok: false };

const unwrap = (data: any): unknown | null => {
  if (!data || typeof data !== 'object') return null;
  // `{ success, scope_key, layout }`, and `layout` is null when unsaved.
  return data.layout ?? null;
};

export async function readRemoteLayout(
  scope: RemoteLayoutScope
): Promise<RemoteReadResult> {
  if (!isStorableScope(scope)) return { ok: false };
  try {
    const response = await apis.get(remoteLayoutPath(scope));
    return { ok: true, layout: unwrap(response?.data) };
  } catch (error: any) {
    // 404 is not in the contract, but a backend that has not shipped the route
    // yet must degrade to local-only rather than log an error every mount.
    const status = error?.response?.status;
    if (status !== 404) {
      logger.debug('[LAYOUT] Remote read failed', {
        scope: `${scope.kind}:${scope.name}`,
        status,
      });
    }
    return { ok: false };
  }
}

/** Full replace. `false` when it did not land — the caller keeps its copy. */
export async function writeRemoteLayout(
  scope: RemoteLayoutScope,
  layout: unknown
): Promise<boolean> {
  if (!isStorableScope(scope)) return false;

  const bytes = layoutByteLength(layout);
  if (bytes > MAX_LAYOUT_BYTES) {
    /*
      Skip rather than send: the server would answer 413 and we would be in
      exactly this branch anyway, one round trip later. The arrangement is
      still in `localStorage`, so this device keeps it — it just stops
      following the user until it shrinks again.
    */
    logger.warn('[LAYOUT] Layout too large to sync; keeping it local only', {
      scope: `${scope.kind}:${scope.name}`,
      bytes,
      limit: MAX_LAYOUT_BYTES,
    });
    return false;
  }

  try {
    await apis.put(remoteLayoutPath(scope), { layout });
    return true;
  } catch (error: any) {
    logger.debug('[LAYOUT] Remote write failed', {
      scope: `${scope.kind}:${scope.name}`,
      status: error?.response?.status,
    });
    return false;
  }
}

/** Reset to default. Idempotent server-side, so a miss is not an error. */
export async function deleteRemoteLayout(scope: RemoteLayoutScope): Promise<boolean> {
  if (!isStorableScope(scope)) return false;
  try {
    await apis.delete(remoteLayoutPath(scope));
    return true;
  } catch (error: any) {
    logger.debug('[LAYOUT] Remote reset failed', {
      scope: `${scope.kind}:${scope.name}`,
      status: error?.response?.status,
    });
    return false;
  }
}
